import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Group,
  Center,
  Stack,
  Text,
  Container,
  rem,
  LoadingOverlay,
  Button,
  ThemeIcon,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import Lottie from "react-lottie-player";
import Microblink from "./microblink/capture/Index";
import VeratadCapture from "./veratad/capture";
import VeratadFaceCapture from "../face-capture/veratad";
import { useParams } from "react-router-dom";
import { DataContext } from "../../context/DataContext";
import InstructionsCarousel from "./instructions-carousel";
import { Carousel } from "@mantine/carousel";
import Mitek from "./mitek/capture";
import { isTablet } from "react-device-detect";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import { getDeviceSignalsUrl } from "../../utils/edge";
import useLocale from "../../hooks/useLocale";
import Handoff from "../handoff";
import CompactHandoff from "../../components/compact-handoff";
import useStatusCheck from "../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../hooks/useEdgeStatusCheck";
import { IconSunHigh, IconId, IconCapture, IconShieldX } from "@tabler/icons-react";
import CenterLoadingOverlay from "../../components/center-loading-overlay";
import IDScan from "../../assets/id-scan-loading.json";
import VerificationLoadingScreen from "../../components/verification-loading-screen";
import StepperLoader from "../../components/stepper-loader";
export default function Dcams({ isModular = false }) {
  const { sdk } = useParams();
  const { data, settings, setSessionState, text } = useContext(DataContext);
  const theme = useMantineTheme();
  const { isTrulyMobile, isEmulated, serverTamperDetected, serverCheckPending } = useDeviceDetection({
    requestId: settings?.request_id,
    serverReportUrl: getDeviceSignalsUrl(),
  });
  const blockedLocale = useLocale("device-blocked");
  const [isSDKActive, setIsSDKActive] = useState(false);
  const [isSDKComplete, setIsSDKComplete] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [getFace, setGetFace] = useState(false);
  const [gotFace, setGotFace] = useState(false);
  const [status, setStatus] = useState("");
  const [onMobile, setOnMobile] = useState(isTrulyMobile);
  const [qrScanned, setQrScanned] = useState(false);
  const language = (
    data?.language ||
    settings?.language ||
    settings?.text?.language ||
    text?.language ||
    ""
  ).toLowerCase();
  const isJapanese = language === "ja";

  // Update onMobile when device detection changes (e.g., emulation toggled)
  useEffect(() => {
    console.log("🔍 DCAMS: isTrulyMobile changed to", isTrulyMobile, "isEmulated:", isEmulated);
    setOnMobile(isTrulyMobile);
  }, [isTrulyMobile, isEmulated]);

  useEffect(() => {
    if (data?.isReactNative) {
      setOnMobile(true);
    }
  }, [data?.isReactNative]);

  useEffect(() => {
    // If showHandoffOnTablet is enabled and user is on tablet, start with QR visible (onMobile = false)
    if (settings?.journey?.settings?.showHandoffOnTablet && isTablet) {
      setOnMobile(false);
    }
  }, [settings?.journey?.settings?.showHandoffOnTablet]);

  const cp = isModular ? "dcams/modular" : "dcams/mitek";
  const isSmallMobile = useMediaQuery("(max-width: 395px)");

  // Use edge status check when edge is true
  if (data?.edge === true) {
    useEdgeStatusCheck(isSDKComplete);
  } else {
    useStatusCheck(isSDKComplete, false, cp);
  }

  useEffect(() => {
    if (isSDKComplete) {
      if (settings.journey.settings?.isSelfie && !gotFace) {
        setIsUploading(false);
        setIsSDKComplete(false);
        setGetFace(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      data.settings = settings;
      data.type = "SDK_Scan";

      // Check if edge mode is enabled
      const edgeQueueUrl =
        process.env.REACT_APP_EDGE_QUEUE_URL ||
        "https://dcams.app/edge/frontend/api/queue/batch";

      const apiUrl = data?.edge === true
        ? edgeQueueUrl
        : "https://vx.dcams.app/frontend/dcams/process";

      // Add queue parameter for edge mode
      const requestBody = data?.edge === true
        ? { ...data, queue: "dcams" }
        : data;

      fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (result) {
          setLoading(false);
        })
        .catch(function () {
          alert("Something Went Wrong! Please try again.");
        });
    }
  }, [isSDKComplete, gotFace]);

  const handleFaceCaptureComplete = () => {
    setIsSDKComplete(true);
    setGotFace(true);
  };

  if (isSDKComplete) {
    // Use new StepperLoader only for Veratad sites
    if (settings?.site_name?.toLowerCase().includes('veratad')) {
      const verificationSteps = [
        {
          title: "Document Analysis",
          description: "Analyzing document structure and format"
        },
        {
          title: "Security Check",
          description: "Verifying security features and authenticity"
        },
        {
          title: "Data Extraction",
          description: "Extracting and processing personal information"
        },
        {
          title: "Final Verification",
          description: "Cross-referencing data with secure databases"
        }
      ];

      return (
        <CenterLoadingOverlay
          content={
            <StepperLoader
              title="Verifying Identity Document"
              steps={verificationSteps}
              stepDuration={1500}
              onComplete={() => {
                // Verification complete - status check will handle navigation
              }}
            />
          }
        />
      );
    }

    // Use original loader for all other sites
    return (
      <CenterLoadingOverlay
        content={
          <VerificationLoadingScreen type="documentVerificationLongDuration" />
        }
      />
    );
  }

  const handleStart = () => {
    setIsSDKActive(true);
  };

  if (loading || isUploading) {
    return <LoadingOverlay visible />;
  }

  // Show loading overlay while server-side device check is pending
  if (serverCheckPending) {
    return <LoadingOverlay visible />;
  }

  // Block session if server-side tamper detection flagged this request
  if (serverTamperDetected) {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          padding: "20px",
        }}
      >
        <ThemeIcon variant="light" color="red" size={80} radius="xl">
          <IconShieldX size={50} stroke={1.5} />
        </ThemeIcon>
        <Text fz={24} fw={700} align="center" mt="lg">
          {blockedLocale?.blocked?.title || "Unable to Continue"}
        </Text>
        <Text fz="md" c="dimmed" align="center" mt="xs" maw={400}>
          {blockedLocale?.blocked?.message || "Please try again on your mobile device."}
        </Text>
      </Box>
    );
  }

  // We now use the onMobile state defined above

  // Check if journey settings enable handoff on tablets
  const showHandoffOnTablet = settings?.journey?.settings?.showHandoffOnTablet === true;
  const shouldShowQROnTablet = showHandoffOnTablet && isTablet;

  // Show handoff with continue button on tablets when showHandoffOnTablet is enabled
  if (shouldShowQROnTablet && !onMobile) {
    return (
      <>
        <Handoff nextPath="dcams/mitek" />
        <Button onClick={() => setOnMobile(true)} mt='sm' fullWidth variant="subtle">
          {text.qr?.troublePrimaryButton || "Continue on this device"}
        </Button>
      </>
    );
  }

  // Check if shouldShowContinueOnDesktop is explicitly set to true in settings
  const should_show_continue_on_desktop = settings?.shouldShowContinueOnDesktop === true;

  // Only show full handoff page if NOT on mobile and shouldShowContinueOnDesktop is false
  if (!onMobile && !should_show_continue_on_desktop) {
    return <Handoff nextPath="dcams/mitek" />;
  }

  const instructions = [
    { Icon: IconSunHigh, instruction: text.dcams.instructions[0] },
    { Icon: IconId, instruction: text.dcams.instructions[1] },
    { Icon: IconCapture, instruction: text.dcams.instructions[2] },
  ];

  return getFace ? (
    <VeratadFaceCapture onCompletion={handleFaceCaptureComplete} />
  ) : (
    <>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // This will push the button to the bottom
          height: "100%", // This should take the full height of its parent
        }}
      >
        <Box>
          <Stack align="center" spacing={(!onMobile && should_show_continue_on_desktop) ? "sm" : "md"}>
            <ThemeIcon
              variant="light"
              c={data.styles?.primaryColor ? data.styles.primaryColor : "teal"}
              size={(!onMobile && should_show_continue_on_desktop) ? 60 : 85}
              radius={"xl"}
            >
              <IconId size={(!onMobile && should_show_continue_on_desktop) ? 45 : 70} stroke={1} />
            </ThemeIcon>

            <Text fz={(!onMobile && should_show_continue_on_desktop) ? 24 : 30} fw={700} align="center">
              {text.dcams.title}
            </Text>
            <Text align="center" fz={(!onMobile && should_show_continue_on_desktop) ? "sm" : undefined}>{text.dcams.subTitle}</Text>
            <Paper
              withBorder
              p={(!onMobile && should_show_continue_on_desktop) ? "sm" : "md"}
              style={{
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : "#fafafa",
                width: "100%"
              }}
            >
              <Stack spacing={(!onMobile && should_show_continue_on_desktop) ? "md" : (isSmallMobile ? "xs" : "lg")}>
                {instructions.map(({ Icon, instruction }, index) => (
                  <Group
                    key={index}
                    spacing={isSmallMobile ? "xs" : "sm"} // Adjust spacing for very small screens
                    noWrap
                  >
                    {/* Render the icon dynamically with the size prop */}
                    <Icon size={(!onMobile && should_show_continue_on_desktop) ? 20 : (isSmallMobile ? 16 : 30)} />

                    {/* Text with responsive font size */}
                    <Text
                      fz={(!onMobile && should_show_continue_on_desktop) ? 14 : (isSmallMobile ? "12px" : "20px")}

                    >
                      {instruction}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Paper>
            {isJapanese && (
              <Text align="center" fz={isSmallMobile ? "12px" : "sm"}>
                <Text span fw={700}>
                  マイナンバーカード裏面の個人番号およびQR
                </Text>
                は、必ず付箋などで隠してスキャンしてください。
              </Text>
            )}
          </Stack>
        </Box>
        <Box>
          <Text fz="sm" color="dimmed" align="center" pb={(!onMobile && should_show_continue_on_desktop) ? 8 : 15}>
            {text.dcams.note}
          </Text>

          <Microblink
            onUpload={() => setIsUploading(true)}
            onComplete={() => setIsSDKComplete(true)}
            onStart={handleStart}
            qrScanned={qrScanned}
          />

          {/* Show compact handoff QR when continue on desktop is enabled and not on mobile */}
          {!onMobile && should_show_continue_on_desktop && !qrScanned && (
            <CompactHandoff
              nextPath="dcams/mitek"
              onQrScanned={setQrScanned}
            />
          )}
        </Box>
      </Box>
    </>
  );
}
